/**
 * Where the portal's credentials live, and the one identifier the API insists on.
 *
 * `X-Device-ID` is not optional. The API's type says `str | None`, but the check underneath it
 * rejects a missing header outright, and every authenticated request is refused without one. So
 * the portal mints a device UUID on first use and keeps it: a stable one means the browser shows
 * up as a single device in the account's device list rather than a new one on every reload, and
 * revoking it in the app signs the portal out — which is the behaviour you want from a device
 * that can read every customer's conversations.
 *
 * Tokens are held in localStorage. That is readable by any script running on this origin, which is
 * the accepted trade for a page that survives a refresh; the mitigation is the origin itself — the
 * portal loads no third-party scripts and is served under a Content-Security-Policy that forbids
 * them. A refresh token in memory only would mean logging in again on every reload, and the
 * failure mode of that is the operator picking a weaker password.
 */

const DEVICE_KEY = 'electra.admin.device';
const SESSION_KEY = 'electra.admin.session';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  organisation_id: string;
  organisation_name: string;
  is_platform_admin: boolean;
}

export interface StoredSession {
  access_token: string;
  refresh_token: string;
  user: AdminUser;
}

/** The same UUID for the life of this browser profile, created the first time it is asked for. */
export function deviceId(): string {
  const existing = window.localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  const minted = crypto.randomUUID();
  window.localStorage.setItem(DEVICE_KEY, minted);
  return minted;
}

/** What the API wants in the `device` field of a login or refresh. */
export function deviceDetails() {
  return { id: deviceId(), name: 'Electra Admin Portal', platform: 'web' as const };
}

export function loadSession(): StoredSession | null {
  const raw = window.localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    // A half-written session is worse than none: it puts the app in a signed-in state whose every
    // request then fails with no way for the operator to get back to the login form.
    if (!parsed?.access_token || !parsed?.refresh_token || !parsed?.user?.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession): void {
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  window.localStorage.removeItem(SESSION_KEY);
}
